import BreadCrumb from "Components/Common/BreadCrumb";
import FullScreenLoader from "Components/Common/FullScreenLoader";
import { StatsCardProps } from "Components/Common/StatsCard";
import StatsGroup from "Components/Common/StatsGroup";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import UserDetailsHeader from "Components/Users/UserDetailsHeader";
import { GlobalImage } from "config/image";
import { useCallback, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import {
  adminDeleteUser,
  initiateImpersonate,
  manageDisableUser,
} from "slices/thunks";
import { ErrorHandler } from "utils/toast";

const UserDetail = () => {
  const loc = useLocation();
  const navigate = useNavigate();
  const { usersList, isLoading: isAdminLoading } = useAppSelector(
    (state) => state.admin
  );
  const userDetail = useMemo(
    () =>
      usersList?.find((user) => user.id === loc.state?.userDetail?.id) ??
      loc.state?.userDetail,
    [usersList, loc.state?.userDetail]
  );
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>("");

  const dashboardStatsData: StatsCardProps[] = useMemo(
    () => [
      {
        title: "TOTAL QUOTES CREATED",
        icon: "BigBlueReceipt",
        value: userDetail?.quotes_count || 0,
        suffix: "",
        prefix: "",
        separator: ",",
        decimal: 0,
      },
      {
        title: "TOTAL INVOICES ISSUED",
        icon: "BigBlueReceipt",
        value: userDetail?.invoices_count || 0,
        suffix: "",
        prefix: "",
        separator: ",",
        decimal: 0,
      },
      {
        title: "TEAM MEMBERS",
        icon: "TotalUsers",
        value: userDetail?.team_count || 0,
        suffix: "",
        prefix: "",
        separator: ",",
        decimal: 0,
      },
      {
        title: "ACCOUNT STATUS",
        icon: "Status",
        suffix: "",
        prefix: "",
        separator: ",",
        decimal: 0,
        status: userDetail?.is_active ? "Active" : "Inactive",
      },
    ],
    [userDetail]
  );

  const handleImpersonate = useCallback(async () => {
    if (!userDetail?.id) return;
    try {
      setIsLoading(true);
      setLoadingMessage("Switching to user account...");
      await dispatch(initiateImpersonate(userDetail.id))?.unwrap();
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Error initiating impersonation:", error);
      ErrorHandler(error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userDetail?.id, navigate]);

  const handleDisable = useCallback(() => {
    dispatch(manageDisableUser(userDetail.id));
  }, [dispatch, userDetail?.id]);

  const handleRemoveUser = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadingMessage("Removing user...");
      await dispatch(adminDeleteUser(userDetail.id))?.unwrap();
      navigate("/users", { replace: true });
    } catch (error) {
      ErrorHandler(error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate, userDetail.id]);

  document.title = "User Detail - EaziQuote";

  // Redirect to dashboard if userDetail is not available
  if (!userDetail) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="page-content">
      <FullScreenLoader isLoading={isLoading} message={loadingMessage} />
      <Container fluid>
        <BreadCrumb title="USER DETAIL" pageTitle="Users" url="/users" />
        <UserDetailsHeader
          name={userDetail.name}
          avatar={userDetail.avatar || GlobalImage.userPlaceHolder}
          company={userDetail.company_name}
          trade={userDetail.trade}
          email={userDetail.email}
          phone={userDetail.phone}
          subscription={userDetail.subscription_status}
          onImpersonate={handleImpersonate}
          onDisable={handleDisable}
          onRemove={handleRemoveUser}
          isDisabled={!userDetail.is_active}
          isDisableLoading={isAdminLoading}
        />

        <StatsGroup items={dashboardStatsData} />
      </Container>
    </div>
  );
};

export default UserDetail;
